/**
 * Mobile Menu
 * Drives the sliding panel stack for the mobile-menu block. Level 1 is the base panel and is
 * visible on load, selecting a parent item pushes its child panel onto the stack so it slides in
 * from the right, and the back button pops it off again.
 */
export default {
  name: 'mobileMenu',
  component(settings) {
    const closeEventName = settings.reset_on_close ? (settings.close_event_name || '').trim() : '';

    return {
      panelStack: [],
      viewportHeight: null,
      slideDuration: settings.slide_duration ?? 300,
      heightMode: settings.panel_height_mode || 'content',
      panelObserver: null,
      closeEventHandler: null,

      init() {
        if (this.isContentHeight) {
          // Keep the viewport height matched to whichever panel is showing so the slide and the
          // height change animate together
          this.$nextTick(() => this.updateViewportHeight());
          this.$watch('panelStack', () => this.updateViewportHeight());

          if (window.ResizeObserver) {
            this.panelObserver = new window.ResizeObserver(() => this.updateViewportHeight());
            this.$el.querySelectorAll('[data-panel-id]').forEach((panel) => {
              this.panelObserver.observe(this.panelContent(panel));
            });
          }
        }

        // Return to level 1 once the containing drawer/popup has closed
        if (closeEventName) {
          this.closeEventHandler = () => {
            window.setTimeout(() => this.reset(), this.slideDuration);
          };
          window.addEventListener(closeEventName, this.closeEventHandler);
        }
      },

      destroy() {
        if (this.panelObserver) {
          this.panelObserver.disconnect();
          this.panelObserver = null;
        }
        if (this.closeEventHandler) {
          window.removeEventListener(closeEventName, this.closeEventHandler);
          this.closeEventHandler = null;
        }
      },

      get isContentHeight() {
        return this.heightMode !== 'fill';
      },

      get basePanel() {
        return this.$el.querySelector('.mobile-menu__panel--base');
      },

      get activePanelId() {
        return this.panelStack.length ? this.panelStack[this.panelStack.length - 1] : null;
      },

      get activePanel() {
        const panelId = this.activePanelId;

        if (!panelId) {
          return this.basePanel;
        }

        return this.$el.querySelector(`[data-panel-id="${panelId}"]`);
      },

      get isChildPanelOpen() {
        return this.panelStack.length > 0;
      },

      // The panel showing on top of the stack, the one the back button returns from
      isPanelActive(panelId) {
        return this.activePanelId === panelId;
      },

      // Every panel in the stack stays put, so a panel opening over the top of another slides in
      // on its own rather than pushing its parent out to the right
      isPanelOpen(panelId) {
        return this.panelStack.includes(panelId);
      },

      openPanel(panelId) {
        if (!panelId || this.isPanelActive(panelId)) {
          return;
        }

        this.panelStack = [...this.panelStack, panelId];
        this.focusActivePanel();
      },

      // Steps back one level. The panel id guards against a stale click from a covered panel.
      closePanel(panelId) {
        if (!this.isChildPanelOpen || (panelId && !this.isPanelActive(panelId))) {
          return;
        }

        this.panelStack = this.panelStack.slice(0, -1);
        this.focusActivePanel();
      },

      reset() {
        if (!this.isChildPanelOpen) {
          return;
        }

        this.panelStack = [];
      },

      focusActivePanel() {
        window.setTimeout(() => {
          const panel = this.activePanel;

          if (panel) {
            panel.focus({ preventScroll: true });
          }
        }, this.slideDuration);
      },

      // The inner wrapper is measured rather than the panel itself, because a panel stretched to
      // the current viewport height can never report a height smaller than the panel before it
      panelContent(panel) {
        return panel.querySelector('.mobile-menu__panel-inner') || panel;
      },

      updateViewportHeight() {
        if (!this.isContentHeight) {
          return;
        }

        const panel = this.activePanel;

        if (!panel) {
          return;
        }

        this.viewportHeight = this.panelContent(panel).scrollHeight;
      }
    };
  }
};
